/**
 * The ai-activity-inbox-api v4 contract, mirroring the Pydantic models in
 * `ai_activity_inbox_api/schemas.py`. Kept as hand-written interfaces rather
 * than generated from the OpenAPI fragment because the fragment describes the
 * subset of the schema published to api-internal, not the full response body.
 *
 * Keep in step with `ACTIVITY_SCHEMA_VERSION` on the backend. v3 moved
 * `disposition` and `rationale` onto each action and made the proposal-level
 * `disposition` a derived roll-up of them; v4 renamed `Citation.quote` to
 * `part_body` and split the router agent identity into UUIDs plus a label.
 */

export type ActivityStatus =
  | 'needs_confirmation'
  | 'in_progress'
  | 'done'
  | 'error'
  | 'abandoned'
  | 'ran_automatically';

/** `none` is a real task with no typed executor (a follow-up call, a research
 * task) — not "nothing to do". A window with nothing actionable comes back as
 * an empty `actions` array instead. */
export type ActionType =
  | 'none'
  | 'options_search'
  | 'create_case'
  | 'escalate'
  | 'calculate_ati';

/** `auto_deny` is proposal-level only: it's what an empty `actions` array
 * derives to, and the backend rejects it on an individual action. */
export type Disposition = 'auto_execute' | 'needs_approval' | 'auto_deny';

export interface Citation {
  part_id: string;
  /** The agent's excerpt of the cited message — not the whole message, which
   * is `MessagePart.body_preview`. Null on activities created before the v4
   * rename, whose stored `quote` the backend no longer reads. */
  part_body?: string | null;
}

export interface ProposedAction {
  action_type: ActionType;
  title: string;
  instruction: string;
  disposition: Disposition;
  rationale: string;
}

export interface RouterProposal {
  title: string;
  description: string;
  actions: ProposedAction[];
  citations: Citation[];
  disposition: Disposition;
}

export interface MessagePart {
  part_id: string;
  author_type: string;
  author_id?: string | null;
  created_at: string;
  body_preview: string;
  part_type: string;
}

export interface EnrichedConversationEvent {
  schema_version: string;
  flush_id: string;
  conversation_id: string;
  client_guid?: string | null;
  channel: string;
  message_count: number;
  window_start: string;
  window_end: string;
  message_parts: MessagePart[];
}

export interface ActivityNote {
  note: string;
  actor: string;
  created_at: string;
}

export interface Activity {
  schema_version: string;
  activity_id: string;
  client_guid?: string | null;
  intercom_conversation_id: string;
  intercom_contact_id?: string | null;
  status: ActivityStatus;
  title: string;
  description: string;
  conversation_link?: string | null;
  /** Router identity, all rendered in the Debug panel. `router_agent_id` and
   * `router_agent_version_id` are Command Center's own UUIDs — search on
   * those. `router_agent_label` is the readable "name-vN" and is the one to
   * show a human. `router_agent_conversation_id` is carried by the contract
   * but nothing populates it yet. */
  router_agent_id?: string | null;
  router_agent_version_id?: string | null;
  router_agent_label?: string | null;
  router_run_id?: string | null;
  router_agent_conversation_id?: string | null;
  router_proposal: RouterProposal;
  enriched_event: EnrichedConversationEvent;
  notes: ActivityNote[];
  created_at: string;
  updated_at: string;
}

export interface CursorPage<T> {
  items: T[];
  next_cursor: string | null;
  previous_cursor: string | null;
  has_more: boolean;
}

export type ReviewAction = 'confirm' | 'retry' | 'deny';

/** Chip styling and copy for each status. `summaryLabel` is the wording used in
 * the count line above the filters, which reads as a sentence rather than as
 * chip labels ("5 awaiting review", not "5 needs confirmation"). */
export interface StatusMeta {
  label: string;
  summaryLabel: string;
  chipClass: string;
  dotClass: string;
}

export const STATUS_META: Record<ActivityStatus, StatusMeta> = {
  needs_confirmation: {
    label: 'Needs FA to confirm',
    summaryLabel: 'awaiting review',
    chipClass: 'bg-amber-50 text-amber-800 border-amber-200',
    dotClass: 'bg-amber-500',
  },
  in_progress: {
    label: 'In progress',
    summaryLabel: 'executing',
    chipClass: 'bg-blue-50 text-blue-800 border-blue-200',
    dotClass: 'bg-blue-500',
  },
  error: {
    label: 'Error',
    summaryLabel: 'failed',
    chipClass: 'bg-red-50 text-red-800 border-red-200',
    dotClass: 'bg-red-500',
  },
  done: {
    label: 'Done',
    summaryLabel: 'done',
    chipClass: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    dotClass: 'bg-emerald-500',
  },
  ran_automatically: {
    label: 'Ran automatically',
    summaryLabel: 'ran automatically',
    chipClass: 'bg-teal-50 text-teal-800 border-teal-200',
    dotClass: 'bg-teal-500',
  },
  abandoned: {
    label: 'Abandoned',
    summaryLabel: 'abandoned',
    chipClass: 'bg-surface-100 text-surface-600 border-surface-300',
    dotClass: 'bg-surface-400',
  },
};

/** Statuses shown as filter chips up front. The rest sit behind "More" so the
 * bar stays scannable — these three are what an FA acts on. */
export const PRIMARY_STATUSES: ActivityStatus[] = [
  'needs_confirmation',
  'in_progress',
  'error',
];

export const SECONDARY_STATUSES: ActivityStatus[] = [
  'done',
  'ran_automatically',
  'abandoned',
];

/** Every status, in the order the "More" menu lists them and the counts are
 * bucketed. */
export const ALL_STATUSES: ActivityStatus[] = [
  ...PRIMARY_STATUSES,
  ...SECONDARY_STATUSES,
];

/** Statuses the API refuses any further action on, mirroring the domain's
 * `_require_open`. Acting on one comes back 409, so the detail dialog offers
 * no buttons rather than letting them fail. Keep in sync with
 * ai-activity-inbox-api's domain/activity.py. */
export const TERMINAL_STATUSES: ActivityStatus[] = [
  'done',
  'error',
  'abandoned',
  'ran_automatically',
];

export const ACTION_TYPE_META: Record<
  ActionType,
  { label: string; chipClass: string }
> = {
  create_case: { label: 'create_case', chipClass: 'bg-blue-100 text-blue-800' },
  escalate: { label: 'escalate', chipClass: 'bg-rose-100 text-rose-800' },
  options_search: {
    label: 'options_search',
    chipClass: 'bg-violet-100 text-violet-800',
  },
  calculate_ati: {
    label: 'calculate_ati',
    chipClass: 'bg-cyan-100 text-cyan-800',
  },
  none: { label: 'none', chipClass: 'bg-surface-200 text-surface-700' },
};

export const DISPOSITION_META: Record<
  Disposition,
  { label: string; chipClass: string }
> = {
  auto_execute: {
    label: 'auto_execute',
    chipClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  needs_approval: {
    label: 'needs_approval',
    chipClass: 'bg-amber-50 text-amber-800 border-amber-200',
  },
  auto_deny: {
    label: 'auto_deny',
    chipClass: 'bg-surface-100 text-surface-600 border-surface-300',
  },
};

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "4 min ago" / "5 hrs ago" / "2 days ago", matching the row and modal copy.
 * Returns an empty string for an unparseable timestamp rather than "NaN ago". */
export function relativeTime(iso: string | undefined | null): string {
  if (!iso) return '';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';

  const elapsed = Date.now() - then;
  if (elapsed < MINUTE) return 'just now';
  if (elapsed < HOUR) {
    const mins = Math.floor(elapsed / MINUTE);
    return `${mins} min ago`;
  }
  if (elapsed < DAY) {
    const hrs = Math.floor(elapsed / HOUR);
    return `${hrs} ${hrs === 1 ? 'hr' : 'hrs'} ago`;
  }
  const days = Math.floor(elapsed / DAY);
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}

/** How long an activity sits in the queue before the row calls it out as
 * waiting. Chosen so anything an FA hasn't touched since yesterday is visibly
 * stale without flagging things proposed this morning. */
const WAITING_THRESHOLD_MS = DAY;

export function isWaiting(activity: Activity): boolean {
  if (activity.status !== 'needs_confirmation') return false;
  const created = Date.parse(activity.created_at);
  return !Number.isNaN(created) && Date.now() - created > WAITING_THRESHOLD_MS;
}

export function isTerminal(activity: Activity): boolean {
  return TERMINAL_STATUSES.includes(activity.status);
}

/** Written per status rather than interpolating STATUS_META.label, which is
 * chip wording and reads wrong in a sentence ("This activity is error"). */
const SETTLED_REASON: Partial<Record<ActivityStatus, string>> = {
  done: 'This activity is done — no further action.',
  error: 'This activity failed — no further action.',
  abandoned: 'This activity was denied — no further action.',
  ran_automatically: 'This activity ran automatically — no further action.',
};

/** The footer line for a closed activity, or null while it is still open. */
export function settledReason(activity: Activity): string | null {
  return SETTLED_REASON[activity.status] ?? null;
}

/** The quote shown under a row title: the first citation that carries one,
 * falling back to the first message in the window so a proposal with no usable
 * citations still shows what it was reacting to. */
export function triggeringQuote(activity: Activity): string | null {
  const cited = activity.router_proposal.citations.find((citation) =>
    citation.part_body?.trim(),
  );
  if (cited?.part_body) return cited.part_body.trim();
  return activity.enriched_event.message_parts[0]?.body_preview?.trim() ?? null;
}

export function actionCountLabel(activity: Activity): string {
  const n = activity.router_proposal.actions.length;
  return `${n} proposed ${n === 1 ? 'action' : 'actions'}`;
}

/** "1 auto_execute · 2 needs_approval" for the proposed-actions header, listing
 * only the dispositions actually present. */
export function dispositionBreakdown(activity: Activity): string {
  const counts = new Map<Disposition, number>();
  for (const action of activity.router_proposal.actions) {
    counts.set(action.disposition, (counts.get(action.disposition) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([disposition, count]) => `${count} ${disposition}`)
    .join(' · ');
}
